import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for animating Emergency Statistics when it comes into view
 */
export default class EmergencyStatistics extends MessageBus {
  constructor(element) {
    super();
    this.element = element;
    this.resizeRaf = null;
    this.hasAnimated = false;
    this.observer = null;

    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();
    this.setEqualHeights();
    this.setupIntersectionObserver();
    window.addEventListener(EVENTS.LOAD, this.setEqualHeights.bind(this));
    return this;
  }

  cacheDomReferences() {
    this.wrapper = this.element.querySelector(
      `.${CLASS_NAMES.EMERGENCY_STATISTICS_WRAPPER}`
    );
    this.cardInners = Array.from(
      this.element.querySelectorAll(
        `.${CLASS_NAMES.EMERGENCY_STATISTICS_CARD_CONTENT}`
      )
    );
    this.cardInnerElements = Array.from(
      this.element.querySelectorAll(
        `.${CLASS_NAMES.EMERGENCY_STATISTICS_CARD_INNER}`
      )
    );
    return this;
  }

  setupHandlers() {
    this.onResize = this.onResize.bind(this);
    this.handleResize = this.handleResize.bind(this);
    return this;
  }

  enable() {
    window.addEventListener(EVENTS.RESIZE, this.onResize);
    return this;
  }

  destroy() {
    window.removeEventListener(EVENTS.RESIZE, this.onResize);
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    if (this.observer) this.observer.disconnect();
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.handleInView(); // Fallback
      return;
    }

    // Clear previous observer
    if (this.observer) {
      this.observer.disconnect();
    }

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add(CLASS_NAMES.IS_IN_VIEWPORT);
              observer.unobserve(entry.target); // Animate only once
            }
          });
        },
        {
          root: null,
          threshold: 1
        }
      );

      this.cardInners.forEach((card) => {
        this.observer.observe(card);
      });
    } else {
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.handleInView();
              observer.disconnect(); // Animate once
            }
          });
        },
        {
          root: null,
          threshold: 1
        }
      );

      if (this.wrapper) {
        this.observer.observe(this.wrapper);
      }
    }
  }

  handleInView() {
    this.cardInners.forEach((card) => {
      card.classList.add(CLASS_NAMES.IS_IN_VIEWPORT);
    });
    this.hasAnimated = true;
  }

  onResize() {
    if (this.resizeRaf) return;
    this.resizeRaf = requestAnimationFrame(this.handleResize);
  }

  handleResize() {
    this.resizeRaf = null;
    this.setEqualHeights();
    this.setupIntersectionObserver(); // Recreate observer on resize
  }

  setEqualHeights() {
    if (window.innerWidth < 768 || !this.cardInnerElements.length) {
      // Reset heights on smaller screens
      this.cardInnerElements.forEach((el) => {
        const element = el;
        element.style.height = 'auto';
      });
      return;
    }

    // Reset heights first
    this.cardInnerElements.forEach((el) => {
      const element = el;
      element.style.height = 'auto';
    });

    const maxHeight = Math.max(
      ...this.cardInnerElements.map((el) => el.offsetHeight)
    );

    this.cardInnerElements.forEach((el) => {
      const element = el;
      element.style.height = `${maxHeight}px`;
    });
  }
}
